import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  login,
  salesAdminRegister,
  salesAdminChangePassword,
  salesAdminForgotPassword,
  salesAdminResetPassword,
} from "../thunk";
import toast from "react-hot-toast";

interface AuthState {
  isLoggedIn: boolean;
  token: string | null;
  uid: string | null;
  id?: string | null;
  status: string;
  adminLevel: string | null;
  error: string;
  firstName: string | null;
  lastName: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  staffId: string | null;
  referralCode?: string | null;
  region?: string | null;
  fullName?: string | null;
  role?: string | null;
  createdAt?: string | null;
}

const storedAuthState = sessionStorage.getItem("authState");
const initialState: AuthState = storedAuthState
  ? JSON.parse(storedAuthState)
  : {
      isLoggedIn: false,
      token: null,
      uid: null,
      status: "idle",
      adminLevel: null,
      error: "",
      firstName: null,
      lastName: null,
      email: null,
      phoneNumber: null,
      staffId: null,
      referralCode: null,
      region: null,
      fullName: null,
      role: null,
    };

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.isLoggedIn = false;
      state.token = null;
      state.uid = null;
      state.adminLevel = null;
      sessionStorage.clear();
    },
  },
  extraReducers: (builder) => {
    builder
      // Regular admin login
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = "";
        state.adminLevel = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoggedIn = true;
        state.token = action.payload.token;
        state.uid = action.payload.admin._id;
        state.id = action.payload.admin.id ?? action.payload.admin._id;
        state.status = "success";
        state.error = "";
        state.adminLevel = action.payload.admin.adminLevel;
        state.firstName = action.payload.admin.firstName;
        state.lastName = action.payload.admin.lastName;
        state.email = action.payload.admin.email ?? null;
        state.phoneNumber = action.payload.admin.phoneNumber ?? null;
        state.staffId = action.payload.admin.staffId;
        state.region = action.payload.admin.region ?? null;
        state.referralCode = action.payload.admin.referralCode ?? null;
        state.createdAt = action.payload.admin.createdAt ?? null;
        {
          const derivedFullName =
            action.payload.admin.fullName ??
            `${action.payload.admin.firstName ?? ""} ${action.payload.admin.lastName ?? ""}`.trim();
          state.fullName = derivedFullName || null;
        }
        sessionStorage.setItem("authState", JSON.stringify(state));
        sessionStorage.setItem(
          "tokenTimestamp",
          new Date().getTime().toString()
        );
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload
          ? action.payload.toString()
          : "Unknown error";
        toast.error("Invalid Credentials");
      })

      // Sales Admin Register
      .addCase(salesAdminRegister.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(
        salesAdminRegister.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoggedIn = true;
          state.token = action.payload.accessToken;
          state.uid = action.payload.user.id;
          state.status = "success";
          state.error = "";
          state.adminLevel = "sales-admin";
          state.fullName = action.payload.user.fullName;
          state.region = action.payload.user.region;
          state.role = action.payload.user.role;
          sessionStorage.setItem("authState", JSON.stringify(state));
          sessionStorage.setItem(
            "tokenTimestamp",
            new Date().getTime().toString()
          );
          toast.success(action.payload.message || "Registration successful");
        }
      )
      .addCase(salesAdminRegister.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload
          ? action.payload.toString()
          : "Unknown error";
        toast.error("Registration failed");
      })

      // Sales Admin Change Password
      .addCase(salesAdminChangePassword.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(
        salesAdminChangePassword.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.status = "success";
          state.error = "";
          toast.success(
            action.payload.message || "Password changed successfully"
          );
        }
      )
      .addCase(salesAdminChangePassword.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload
          ? action.payload.toString()
          : "Unknown error";
        toast.error(action.payload?.toString() || "Failed to change password");
      })

      // Sales Admin Forgot Password
      .addCase(salesAdminForgotPassword.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(
        salesAdminForgotPassword.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.status = "success";
          state.error = "";
          toast.success(action.payload.message || "OTP sent to your email");
        }
      )
      .addCase(salesAdminForgotPassword.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload
          ? action.payload.toString()
          : "Unknown error";
        toast.error(action.payload?.toString() || "Failed to process request");
      })

      // Sales Admin Reset Password
      .addCase(salesAdminResetPassword.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(
        salesAdminResetPassword.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.status = "success";
          state.error = "";
          toast.success(action.payload.message || "Password reset successful");
        }
      )
      .addCase(salesAdminResetPassword.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload
          ? action.payload.toString()
          : "Unknown error";
        toast.error(action.payload?.toString() || "Failed to reset password");
      });
  },
});

export const { logout } = authSlice.actions;

export default authSlice.reducer;
